import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CreditCard, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { readApiJson } from '../lib/api';

// Tells an account once that its subscription payment failed (the renewal
// didn't go through, so full access is paused -- see getEffectivePlan
// Presentation), or that Stripe ended the subscription after the payments
// kept failing. /account/access carries the notice, with a key naming this
// particular failure; dismissing it (the button, the X) records the key on
// the account, so it doesn't come back -- on any browser, or in the app --
// unless a later renewal fails. The Account page keeps its own, permanent
// warning for as long as the payment is unresolved.
const NOTICES = {
  payment_failed: {
    label: 'Payment failed',
    message: 'Your subscription renewal didn’t go through, so your full access is paused. Update your payment method to restore it.',
    action: 'Update payment method'
  },
  subscription_ended: {
    label: 'Subscription ended',
    message: 'Your subscription ended because payment failed. Restart it to get full access back.',
    action: 'Restart subscription'
  }
};

const dismissedSessionKey = (userId, key) => `cbat_billing_notice_dismissed:${userId}:${key}`;

const readDismissed = (storageKey) => {
  try {
    return sessionStorage.getItem(storageKey) === '1';
  } catch (error) {
    return false;
  }
};

const BillingNoticeBanner = () => {
  const location = useLocation();
  const { user, accessState, hasFullAccess, refreshAccess } = useAuth();
  const [dismissedKey, setDismissedKey] = useState(null);
  const [busy, setBusy] = useState(false);

  const notice = accessState?.billing_notice;
  const details = notice ? NOTICES[notice.kind] : null;
  if (!user || !details || hasFullAccess || location.pathname.startsWith('/module/')) return null;

  const storageKey = dismissedSessionKey(user.id, notice.key);
  if (dismissedKey === notice.key || readDismissed(storageKey)) return null;

  // Hidden at once, and for the rest of this session even if the server
  // can't be reached right now.
  const markSeen = async () => {
    setDismissedKey(notice.key);
    try {
      sessionStorage.setItem(storageKey, '1');
    } catch (error) {
      // Storage can throw in a private window; the state above still hides it.
    }
    try {
      await readApiJson('/account/billing-notice/dismiss', {
        method: 'POST',
        body: JSON.stringify({ key: notice.key })
      });
      refreshAccess();
    } catch (error) {
      // Seen on this device for now; it may show once more on the next visit.
    }
  };

  const openBillingPortal = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const data = await readApiJson('/account/billing-portal', {
        method: 'POST',
        body: JSON.stringify({ return_url: `${window.location.origin}/settings` })
      });
      await markSeen();
      window.location.href = data.url;
    } catch (error) {
      // The banner stays, so it can be tried again.
      toast.error(error.message || 'Unable to open billing right now. Try Manage Billing on your account page.');
      setBusy(false);
    }
  };

  return (
    <section
      className="billing-notice-banner border-b border-[#7a3340] bg-[#2a0f16]/95"
      aria-label={details.label}
      aria-live="polite"
      data-testid="billing-notice-banner"
      data-notice-kind={notice.kind}
    >
      <div className="mx-auto flex max-w-[1320px] flex-col gap-3 px-4 py-3 text-white sm:flex-row sm:items-center sm:px-6">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-current bg-black/20 text-[#ff7b87]">
            <CreditCard size={17} aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <span className="font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-[#ff7b87]">
              {details.label}
            </span>
            <p className="mt-1 text-sm leading-relaxed text-[#edf5ff]">{details.message}</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2 self-end sm:self-auto">
          {notice.kind === 'payment_failed' ? (
            <button
              type="button"
              onClick={() => void openBillingPortal()}
              disabled={busy}
              className="site-button site-button--primary !min-h-8 shrink-0 !py-1.5"
              data-testid="billing-notice-action"
            >
              {busy ? 'Please wait...' : details.action}
            </button>
          ) : (
            <Link
              to="/subscription"
              onClick={() => void markSeen()}
              className="site-button site-button--primary !min-h-8 shrink-0 !py-1.5"
              data-testid="billing-notice-action"
            >
              {details.action}
            </Link>
          )}
          <button
            type="button"
            onClick={() => void markSeen()}
            title="Dismiss"
            aria-label="Dismiss billing notice"
            className="site-icon-button h-8 w-8 shrink-0"
            data-testid="billing-notice-dismiss"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default BillingNoticeBanner;
